/**
 * MedMic-only auth snapshot. Enterprise CRE Zustand is not the MedMic SSoT.
 */

import { create } from "zustand";

/** @typedef {"signed_out" | "authenticating" | "validating" | "authenticated" | "validation_failed" | "disconnecting" | "disconnected" | "refreshing" | "expired" | "refresh_failed" | "interaction_required"} MedMicAuthState */

/**
 * @typedef {object} MedMicAuthSnapshot
 * @property {MedMicAuthState} state
 * @property {number} epoch
 * @property {string} sessionId
 * @property {number} generation
 * @property {boolean} disconnected
 * @property {string} [accessToken]
 * @property {string | null} [userEmail]
 * @property {number | null} [expiresAt]
 * @property {string} [operationId]
 * @property {{ code?: string, message?: string } | null} [error]
 * @property {Record<string, unknown> | null} [user]
 * @property {string | null} [homeAccountId]
 * @property {string | null} [localAccountId]
 * @property {string | null} [reason]
 * @property {string | null} [errorCode]
 * @property {"proactive" | "http_401" | "bootstrap" | "connect" | null} [refreshSource]
 * @property {boolean} [refreshWarning]
 */

function createSessionId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `medmic-${Date.now()}`;
}

/** @returns {MedMicAuthSnapshot} */
export function createInitialMedMicAuthSnapshot() {
  return {
    state: "signed_out",
    epoch: 0,
    sessionId: createSessionId(),
    generation: 0,
    disconnected: false,
    accessToken: undefined,
    userEmail: null,
    expiresAt: null,
    operationId: undefined,
    error: null,
    user: null,
    homeAccountId: null,
    localAccountId: null,
    reason: null,
    errorCode: null,
    refreshSource: null,
    refreshWarning: false,
  };
}

export const useMedMicAuthStore = create(() => createInitialMedMicAuthSnapshot());

/** @returns {MedMicAuthSnapshot} */
export function getMedMicAuthSnapshot() {
  return useMedMicAuthStore.getState();
}

/**
 * @param {Partial<MedMicAuthSnapshot>} patch
 */
export function patchMedMicAuthStore(patch) {
  useMedMicAuthStore.setState(patch);
}

/** Replace entire snapshot (remote sync / tests). */
export function replaceMedMicAuthStore(snapshot) {
  useMedMicAuthStore.setState(snapshot, true);
}

/** Test helper — resets module store to a fresh signed_out session. */
export function resetMedMicAuthStoreForTests() {
  useMedMicAuthStore.setState(createInitialMedMicAuthSnapshot(), true);
}

/**
 * Ordering: epoch first, then generation.
 * @param {{ epoch: number, generation: number }} a
 * @param {{ epoch: number, generation: number }} b
 * @returns {number} negative if a < b, 0 if equal, positive if a > b
 */
export function compareMedMicSessionOrder(a, b) {
  const epochA = typeof a.epoch === "number" ? a.epoch : 0;
  const epochB = typeof b.epoch === "number" ? b.epoch : 0;
  if (epochA !== epochB) return epochA - epochB;
  const genA = typeof a.generation === "number" ? a.generation : 0;
  const genB = typeof b.generation === "number" ? b.generation : 0;
  return genA - genB;
}

export function createMedMicSessionId() {
  return createSessionId();
}
